/* eslint-disable react-hooks/exhaustive-deps */
import { Alert, Button, Stack, styled, Theme, Typography } from '@ethings/ui-toolkit';
import { Link, useTheme } from '@mui/material';
import { captureException } from '@sentry/react';
import chroma from 'chroma-js';
import { useSnackbar } from 'notistack';
import { FC, useEffect, useState, type JSX } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AuthStatus, useAuth } from '../../auth';
import { LoadingButton } from '../../components/loading-button';
import { EthingsLogo, EthingsPoweredByLogo } from '../../components/logo';
import { useConfig, useResponsiveText, useSmallScreen, useTermsAndConditionsLabel } from '../../hooks';
import { LandingView } from './view';

interface LandingControllerProps {
  portalName: string;
}

const StyledAlert = styled(Alert)({
  width: '100%',
  '& .MuiAlert-message': {
    width: '100%',
  },
});

const StyledButton = styled(Button)(({ theme }: { theme: Theme }) => {
  const customTheme = theme as typeof theme & {
    landingPage: { signInButton: { contrastText: string; background: string } };
  };
  return {
    color: customTheme.landingPage.signInButton.contrastText,
    backgroundColor: customTheme.landingPage.signInButton.background,
    '&:hover': {
      backgroundColor: chroma(customTheme.landingPage.signInButton.background).darken(0.5).hex(),
    },
  };
});

export const LandingPageController: FC<LandingControllerProps> = ({ portalName }) => {
  const { termsOfService, productName } = useConfig();
  const { signIn, signOut, state } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const smallScreen = useSmallScreen();
  const { hasOverride, termsLabel } = useTermsAndConditionsLabel();
  const { i18n, t } = useTranslation(['common']);
  const theme = useTheme();
  const [configError, setConfigError] = useState(false);
  const signInParam = 'signIn';

  const h3Styles = useResponsiveText('3rem', 30, {
    minFontSize: '2rem',
    maxFontSize: '3rem',
    textLengthThreshold: 40,
  });

  const h5Styles = useResponsiveText('1.5rem', 25, {
    minFontSize: '1.25rem',
    maxFontSize: '1.5rem',
    textLengthThreshold: 25,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectAfterSignOut = params.get(signInParam);
    if (redirectAfterSignOut !== 'true') return;
    setConfigError(true);
    window.history.replaceState(null, '', window.location.pathname);
    signIn()
      .then(() => {
        setConfigError(false);
      })
      .catch((err) => {
        console.error(err);
        captureException(err);
      });
  }, []);

  useEffect(() => {
    if (state?.status === AuthStatus.CONFIG_ERROR) {
      setConfigError(true);
      return;
    }
    if (state?.status === AuthStatus.AUTHENTICATION_FAILED) {
      enqueueSnackbar(i18n.exists(`auth-errors:${state.failure.error}`)
        ? t(`auth-errors:${state.failure.error}`)
        : state.failure.errorDescription, { variant: 'error' });
    }
  }, [state]);

  const renderLogo = (logoUrl: string): JSX.Element => (
    logoUrl.length ? <img src={logoUrl} alt={productName ? `${productName} logo` : 'logo'} width="100%" /> : <EthingsLogo logo={theme.landingPage.logo} width='250px' />
  );

  const renderHeader = (headerText: string): JSX.Element => (
    <Typography
      id="header"
      variant="h3"
      whiteSpace='pre-line'
      style={{
        fontSize: h3Styles.fontSize,
        lineHeight: h3Styles.lineHeight,
        wordBreak: h3Styles.wordBreak,
        whiteSpace: h3Styles.whiteSpace,
        overflow: h3Styles.overflow,
        textOverflow: h3Styles.textOverflow,
      }}
    >
      {headerText}
    </Typography>
  );

  const renderSubHeader = (subHeaderText: string): JSX.Element => (
    <Typography
      id='sub-header'
      variant="h5"
      style={{
        fontSize: h5Styles.fontSize,
        lineHeight: h5Styles.lineHeight,
        wordBreak: h5Styles.wordBreak,
        whiteSpace: h5Styles.whiteSpace,
        overflow: h5Styles.overflow,
        textOverflow: h5Styles.textOverflow,
      }}
    >
      {subHeaderText}
    </Typography>
  );

  const renderSignInButton = (): JSX.Element => (
    <Stack spacing={3} alignItems='center' width={smallScreen ? '100%' : 'auto'}>
      {!configError && (
        <LoadingButton
          data-testid='sign-in-button'
          fullWidth={smallScreen}
          size='large'
          task={() => signIn()}
          variant='contained'
          sx={{
            color: theme.landingPage.signInButton.contrastText,
            backgroundColor: theme.landingPage.signInButton.background,
            '&:hover': {
              backgroundColor: chroma(theme.landingPage.signInButton.background).darken(0.5).hex(),
            },
          }}
        >
          {t('common:component.auth.labels.sign-in')}
        </LoadingButton>
      )}
      {state?.status === AuthStatus.CONFIG_ERROR && (
        <Stack spacing={2} alignItems='center' width="100%">
          <StyledAlert
            data-testid="config-error"
            severity="error"
          >
            {t('common:component.auth.error.config-error')}
          </StyledAlert>
          <StyledButton
            data-testid='sign-in-again-button'
            onClick={() => signOut(signInParam)}
            fullWidth={smallScreen}
            size='large'
            variant='contained'
          >
            {t('common:component.auth.labels.sign-in-again')}
          </StyledButton>
        </Stack>
      )}
    </Stack>
  );

  const renderFooterLogo = (logoUrl: string): JSX.Element => (
    <Stack id="footer-logo" direction="row">
      {logoUrl.length ?
        <img src={logoUrl} width="100px" alt="Footer logo" />
        : <>
          <EthingsPoweredByLogo logo={theme.landingPage.footer.logo} width='120px' />
        </>}
    </Stack>
  );

  const renderTopFooterText = (topFooterText: string): JSX.Element => (
    <Typography id="footer-top-text" color={theme.landingPage.footer.contrastText} variant="caption">{topFooterText}</Typography>
  );

  const renderBottomFooterText = (bottomFooterText: string): JSX.Element => {
    return <Typography id="footer-bottom-text" color={theme.landingPage.footer.contrastText} variant="caption">{bottomFooterText}</Typography>;
  };

  const renderTermsAndConditionsText = (): JSX.Element => {
    if (!termsOfService?.documentId || !productName) return <></>;
    const origin = window.location.origin;
    const domain = window.location.host;
    const termsLink = `${origin}/web-asset/${termsOfService.documentId}?domain=${domain}`;

    return (
      <Stack alignItems="center" mt={2}>
        <Typography
          variant="body2"
          data-testid="terms-and-conditions-text"
          color={theme.landingPage.footer.contrastText}
        >
          {hasOverride ? (
            <Trans
              i18nKey="common:component.landing.labels.account-terms-and-conditions-v2"
              components={[
                <Link
                  key="link"
                  href={termsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${productName} terms of service (opens in new tab)`}
                  sx={{
                    color: theme.landingPage.footer.contrastText,
                    textDecoration: 'underline',
                    '&:hover': {
                      color: theme.palette.primary.dark,
                    },
                  }}
                />,
              ]}
              values={{ account: productName, termsLabel }}
            />
          ) : (
            <Trans
              i18nKey="common:component.landing.labels.account-terms-and-conditions"
              components={[
                <Link
                  key="link"
                  href={termsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${productName} terms of service (opens in new tab)`}
                  sx={{
                    color: theme.landingPage.footer.contrastText,
                    textDecoration: 'underline',
                    '&:hover': {
                      color: theme.palette.primary.dark,
                    },
                  }}
                />,
              ]}
              values={{ account: productName }}
            />
          )}
        </Typography>
      </Stack>
    );
  };

  return (
    <LandingView
      portalName={portalName}
      renderBottomFooterText={renderBottomFooterText}
      renderFooterLogo={renderFooterLogo}
      renderHeader={renderHeader}
      renderLogo={renderLogo}
      renderSignInButton={renderSignInButton}
      renderSubHeader={renderSubHeader}
      renderTermsAndConditionsText={renderTermsAndConditionsText}
      renderTopFooterText={renderTopFooterText}
      smallScreen={smallScreen}
    />
  );
};
